import UserModel from '../model/user';
import VideoUrlModel from '../model/video_url';
import BannerZoneModel from '../model/banner_zone';
import BookmarkGroupsModel from '../model/bookmarks';
import ProfileModel from '@core/model/profile';
import GqlError from '@core/model/gql_error';
import TvCodeModel from '../model/tvCode';
import GqlData from './gql_data';
import PagehitData from './pagehit_data';
import { loginInfoQuery, loginInfoGqlc, registerEmailOnlyQuery, loginUserQuery, loginProfileQuery, logoutUserQuery, linkDeviceToUser, loginWithDeviceQuery } from './queries/user';
import { videoUrlQuery, epgHlsUrlQuery } from './queries/video_url';
import { commentVoteUpQuery, commentVoteDownQuery, commentAddQuery } from './queries/comments';
import { jokeVoteUpQuery, jokeVoteDownQuery } from './queries/jokes';
import { pollVoteQuery } from './queries/poll';
import { fetchBannersQuery } from './queries/banners';
import { recipeBookmarkAddToGroup, recipeBookmarkDelete, recipeBookmarkGroupAddRename, recipeBookmarkGroupDelete, voyoBookmarks, voyoBookmarkDelete, voyoBookmarkAdd } from './queries/bookmarks';
import { categoryStartWith, voyoCategoryQuery } from './queries/category';
import { videoQuery } from './queries/video';
import { liveStreamQuery } from './queries/live_stream';
import { userProfiles, deleteProfile, updateProfile, addProfile } from './queries/profiles';
import { tvGetCode } from './queries/device';
import CategoryStartWith from '../model/category_start_with';
import AppOptions from '../app/options';
import {fetchWithTimeout} from '../libs/fetch';
import EpgHlsUrlModel from '@core/model/epg_hls_url';
import VoyoCategoryModel from '@core/model/voyo_category';
import VideoModel from '@core/model/video';
import LiveStreamModel from '@core/model/live_stream';
import IspLoginCheckModel from '@core/model/isp_login';
import { ispLoginCheck, ispLoginJob } from './queries/isp_login';
import retryOnce from '@core/libs/retry';
import IspLoginJobModel from '@core/model/isp_login_job';

class GQL {
    public authToken: string = '';
    public deviceId: string = '';

    constructor(private options: AppOptions) { }

    // gqlc is cached on cdn for 5 seconds. set useGqlc = false if that is too long
    // for your use case.
    loginInfo = async (token: string, siteId: number, useGqlc = true): Promise<UserModel> => {
        if (useGqlc) {
            const url = loginInfoGqlc(token, siteId);

            return retryOnce(() =>
                this.fetchGQL(null, { method: 'GET', url, headers:{Authorization: token} })
            )
            .then(loginInfo => new UserModel(loginInfo));
        }

        const gqlData = loginInfoQuery(token, siteId);
        return this.fetchGQL(gqlData)
            .then(loginInfo => new UserModel(loginInfo));
    }

    loginUser = async (email: string, password: string, siteId: number): Promise<UserModel> => {
        const gqlData = loginUserQuery(email, password, siteId);
        return this.fetchGQL(gqlData)
            .then(login => new UserModel(login));
    }

    loginWithDevice = async (deviceName: string, deviceFamily: string, siteId: number): Promise<UserModel> => {
        const gqlData = loginWithDeviceQuery(deviceName, deviceFamily, siteId);
        return this.fetchGQL(gqlData)
            .then(loginDevice => new UserModel(loginDevice));
    }

    logoutUser = async (): Promise<boolean> => {
        const gqlData = logoutUserQuery();
        return this.fetchGQL(gqlData)
            .then(logout => logout ? true : false);
    }

    loginProfile = async (profileId: number): Promise<UserModel> => {
        const gqlData = loginProfileQuery(profileId);
        return this.fetchGQL(gqlData)
            .then(loginProfile => new UserModel(loginProfile));
    }

    newProfile = async (name: string, profileType: string, avatar: string): Promise<any> => {
        const gqlData = addProfile(name, profileType, avatar);
        return this.fetchGQL(gqlData);
    }

    updateProfile = async (profileId: number, name: string, avatar: string): Promise<any> => {
        const gqlData = updateProfile(profileId, name, avatar);
        return this.fetchGQL(gqlData);
    }

    deleteProfile = async (profileId: number): Promise<any> => {
        const gqlData = deleteProfile(profileId);
        return this.fetchGQL(gqlData);
    }

    registerEmailOnly = async (email: string, siteId: number, mailingId: number, sendUnsubscribeEmail: boolean): Promise<any> => {
        const gqlData = registerEmailOnlyQuery(email, siteId, mailingId, sendUnsubscribeEmail);
        return this.fetchGQL(gqlData);
    }

    voyoBookmarks = async (): Promise<BookmarkGroupsModel> => {
        const gqlData = voyoBookmarks();
        return this.fetchGQL(gqlData)
            .then(voyoBookmark => new BookmarkGroupsModel(voyoBookmark));
    }

    voyoBookmarkRemove = async (groupId: string, itemId: string): Promise<boolean> => {
        const gqlData = voyoBookmarkDelete(groupId, itemId);
        return this.fetchGQL(gqlData)
            .then(voyoBookmarkDelete => voyoBookmarkDelete ? true : false)
            .catch(() => false); // Mainly there's a call to remove item from stayedAt and it has already been removed.
    }

    voyoBookmarkAdd = async (groupId: string, itemId: string, percent = 0, duration = 0): Promise<boolean> => {
        const gqlData = voyoBookmarkAdd(groupId, itemId, percent, duration);
        return this.fetchGQL(gqlData, {keepalive: true, timeout: 4000})
            .then(voyoBookmarkAdd => voyoBookmarkAdd ? true : false)
            .catch(() => false); // voyoBookmarkAdd timeouts a lot, so we make it quiet here
    }

    voyoProfiles = async (): Promise<Array<ProfileModel>> => {
        const gqlData = userProfiles();
        return this.fetchGQL(gqlData)
            .then(userProfiles => {
                if (!userProfiles) {
                    return [];
                }
                return userProfiles.profiles.map((p: any) => new ProfileModel(p));
            });
    }

    tvGetCode = async (deviceName: string, deviceFamily: string): Promise<TvCodeModel> => {
        const gqlData = tvGetCode(deviceName, deviceFamily);
        return this.fetchGQL(gqlData)
            .then(tvGetCode => {
                console.log('CODE', tvGetCode)
                return new TvCodeModel(tvGetCode);
            });
    }

    ispLoginCheck = async (jobHash: string): Promise<IspLoginCheckModel> => {
        const gqlData = ispLoginCheck(jobHash);
        return this.fetchGQL(gqlData)
            .then(ispLoginCheck => new IspLoginCheckModel(ispLoginCheck));
    }

    ispLoginJob = async (provider: string, a1Token: string): Promise<IspLoginJobModel> => {
        const gqlData = ispLoginJob(provider, a1Token);
        return this.fetchGQL(gqlData)
            .then(ispLogin => new IspLoginJobModel(ispLogin));
    }

    categoryStartWith = async (categoryId: number): Promise<CategoryStartWith> => {
        const gqlData = categoryStartWith(categoryId);
        return this.fetchGQL(gqlData)
            .then(voyoCategory => new CategoryStartWith(voyoCategory.startWith));
    }

    videoUrl = async (id: number): Promise<VideoUrlModel> => {
        const gqlData = videoUrlQuery(id, this.options.siteId);

        return this.fetchGQL(gqlData)
            .then(videoUrl => new VideoUrlModel(videoUrl));
    }

    linkDeviceToUser = async (family: string, name: string, model: string): Promise<UserModel> => {
        const gqlData = linkDeviceToUser(family, name, model);
        return this.fetchGQL(gqlData)
            .catch(err => {
                if (err instanceof GqlError && err.message.includes('max device limit reached')) {
                    throw new Error('too_many_devices');
                }
            })
            .then(linkDeviceToUser => new UserModel(linkDeviceToUser));
    }

    epgHlsUrl = async (channel: string, chunkStart: number, chunkEnd: number): Promise<EpgHlsUrlModel> => {
        const gqlData = epgHlsUrlQuery(channel, chunkStart, chunkEnd);

        return this.fetchGQL(gqlData)
            .then(epgHlsUrl => new EpgHlsUrlModel(epgHlsUrl));
    }

    commentVoteUp = async (articleId: number, commentId: string): Promise<any> => {
        const gqlData = commentVoteUpQuery(articleId, commentId);
        return this.fetchGQL(gqlData);
    }

    commentVoteDown = async (articleId: number, commentId: string): Promise<any> => {
        const gqlData = commentVoteDownQuery(articleId, commentId);
        return this.fetchGQL(gqlData);
    }

    commentAdd = async (articleId: number, text: string, replyTo: string): Promise<any> => {
        const gqlData = commentAddQuery(articleId, text, replyTo);
        return this.fetchGQL(gqlData);
    }

    jokeVoteUp = async (jokeId: number): Promise<any> => {
        const gqlData = jokeVoteUpQuery(jokeId);
        return this.fetchGQL(gqlData);
    }

    jokeVoteDown = async (jokeId: number): Promise<any> => {
        const gqlData = jokeVoteDownQuery(jokeId);
        return this.fetchGQL(gqlData);
    }

    pollVote = async (pollId: number, answerId: number, gender: string): Promise<any> => {
        const gqlData = pollVoteQuery(pollId, answerId, gender);
        return this.fetchGQL(gqlData);
    }

    recipeBookmarkDelete = async (groupId: string, itemId: string, ): Promise<any> => {
        const gqlData = recipeBookmarkDelete(groupId, itemId)
        return this.fetchGQL(gqlData);
    }

    recipeBookmarkGroupDelete = async (groupId: string): Promise<any> => {
        const gqlData = recipeBookmarkGroupDelete(groupId)
        return this.fetchGQL(gqlData);
    }

    recipeBookmarkGroupAddRename = async (groupId: string, name: string): Promise<any> => {
        const gqlData = recipeBookmarkGroupAddRename(groupId, name)
        return this.fetchGQL(gqlData);
    }

    recipeBookmarkAddToGroup = async (groupId: string, itemId: string): Promise<any> => {
        const gqlData = recipeBookmarkAddToGroup(groupId, itemId)
        return this.fetchGQL(gqlData);
    }

    voyoCategory = async (voyoCategoryId: number): Promise<VoyoCategoryModel> => {
        const gqlData = voyoCategoryQuery(voyoCategoryId);
        return this.fetchGQL(gqlData)
            .then(voyoCategory => new VoyoCategoryModel(voyoCategory));

    }

    video = async (mediaId: number): Promise<VideoModel> => {
        const gqlData = videoQuery(mediaId);
        return this.fetchGQL(gqlData)
            .then(video => new VideoModel(video));
    }

    liveStream = async (liveStreamId: number): Promise<LiveStreamModel> => {
        const gqlData = liveStreamQuery(liveStreamId);
        return this.fetchGQL(gqlData)
            .then(liveStream => new LiveStreamModel(liveStream));
    }

    pageHit = async (data: PagehitData): Promise<any> => {
        const url = this.options.eventsUrl + '/pagehitandevent';
        return this.fetchGQL(data, { url, skipCleanup: true });
    }

    fetchBanners = async (params: {
        affiliateId: number,
        categories: string,
        sectionId: number,
        cookieAccept: string,
        responseJSON: string,
        hasVast: number
    }): Promise<{ banners: Array<BannerZoneModel>, adsStoreUrl: string }> => {
        const url = fetchBannersQuery(params, this.options.adserverUrl);
        return this.fetchGQL(null, { method: 'GET', url, skipCleanup: true })
            .then(response => {
                return {
                    banners: response.data ? response.data as Array<BannerZoneModel> : [],
                    adsStoreUrl: response.adsURL,
                };
            });
    }

    private fetchGQL(data: GqlData | PagehitData | null, options: any = {}): Promise<any> {
        const method = options.method || 'POST';
        const defaultHeaders = {
            'Content-Type': 'application/json',
            'Authorization': this.authToken || options.headers?.Authorization || '',
            'Device-Id': this.deviceId,
            'Onl-Location': document.location.href,
        };
        const headers = Object.assign(defaultHeaders)
        const body = (method === 'POST') ? JSON.stringify(data) : null;
        const keepalive = options.keepalive || false;

        let url = options.url || this.options.graphQL;
        if (!url.startsWith('http')) {
            url = this.options.graphQL + url;
        }

        const timeout = options.timeout || 2000;

        return fetchWithTimeout(url, {method,headers,body,timeout,keepalive})
                .then(async (response) => {
                    if (!response.ok) {
                        const text = await response.text();
                        throw new Error('GQL:' + text);
                    }
                    return response.json();
                })
                .then((payload) => {
                    // If this was not a call to GQL than there's no need to
                    // clean it up.
                    if (options.skipCleanup) {
                        return payload;
                    }

                    // We need the first (and only) element from inside data
                    const values = Object.keys(payload.data || {}).map(key => payload.data[key]);
                    const gqlData = values.length ? values[0] : null;

                    // Throw error if we got errors and no valid data
                    if (!gqlData && payload.errors) {
                        (window as any).Sentry?.setContext("gqlError", {url, method,headers,body,timeout,keepalive});

                        throw new GqlError(payload.errors);
                    }

                    return gqlData;
                })
                .catch((error) => {
                    console.warn('Fetch error:', error);
                    throw error; // Re-throw to keep it as a rejection
                });
    }
}

export default GQL;